import { useThemeStore } from "@stores/theme.store";
import { usePageHeader } from "@hooks/use_page_header";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";
import { PopupConfirm, PopupCustom, PopupSuccess } from "@components/general/popup";
import Cookies from "js-cookie";
import { useStudentBio } from "@hooks/use_student_bio";
import { useStudentBiography } from "@hooks/use_student_biography";
import { androidSetupBiometric } from "@utils/android";
import { useBiometricStore } from "@stores/biometric.store";
import {
  LuChevronRight,
  LuFingerprint,
  LuKeyRound,
  LuLogOut,
  LuScanLine,
  LuUserCog,
  LuUsers,
} from "react-icons/lu";
import { Button, Select, message } from "antd";
import { NoticeType } from "antd/es/message/interface";
import JsBarcode from "jsbarcode";
import MaleLogo from "@assets/images/avatar.png";
import FemaleLogo from "@assets/images/avatar-female.png";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AccountService } from "@services/account.service";

const SectionLabel = ({ title }: { title: string }) => (
  <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2">
    <div className="tw-w-1 tw-h-4 tw-rounded-full tw-bg-primary tw-shrink-0" />
    <div className="tw-text-[10px] tw-font-bold tw-text-text-tertiary tw-uppercase tw-tracking-widest">
      {title}
    </div>
  </div>
);

type MenuRowProps = {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  sub: string;
  onClick: () => void;
  danger?: boolean;
};

const MenuRow = ({
  icon,
  iconBg,
  label,
  sub,
  onClick,
  danger,
}: MenuRowProps) => (
  <div
    className="tw-flex tw-items-center tw-gap-3 tw-py-3 tw-cursor-pointer"
    onClick={onClick}
  >
    <div
      className="tw-flex tw-items-center tw-justify-center tw-rounded-2xl tw-w-10 tw-h-10 tw-shrink-0"
      style={{ background: iconBg }}
    >
      {icon}
    </div>
    <div className="tw-flex-1 tw-min-w-0">
      <div
        className={`tw-text-sm tw-font-semibold tw-truncate ${danger ? "tw-text-red-500" : "tw-text-text-primary"}`}
      >
        {label}
      </div>
      <div className="tw-text-[11px] tw-text-text-tertiary tw-truncate">
        {sub}
      </div>
    </div>
    <LuChevronRight
      className={`tw-shrink-0 tw-text-sm ${danger ? "tw-text-red-400" : "tw-text-background-400"}`}
    />
  </div>
);

const Divider = () => (
  <div className="tw-h-px" style={{ background: "#f1f5f9" }} />
);

const BIOMETRIC_ENABLED = false;

export const ProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAppBar, popupStatus, setPopupStatus } = useThemeStore(
    (state) => state,
  );
  usePageHeader("Profil");
  const [isBiometric, setIsBiometric] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [popupStudent, setPopupStudent] = useState<boolean>(false);
  const [popupSuccessStudent, setPopupSuccessStudent] =
    useState<boolean>(false);
  const [studentNis, setStudentNis] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const { isActiveBiometric } = useBiometricStore((state) => state);

  const {
    nis,
    nama,
    isSuccess: isSuccessNis,
    rawData: dataNisData,
  } = useStudentBio();
  const { biography, isPending, gender } = useStudentBiography();

  const { data: students } = useQuery({
    queryKey: [AccountService.queries.GET_ACCOUNT_STUDENTS],
    queryFn: AccountService.students,
  });

  const { mutate: mutateChangeStudent, isPending: isPendingChangeStudent } =
    useMutation({
      mutationFn: AccountService.changeStudent,
      onSuccess: (response) => {
        if (response.success) {
          setPopupStudent(false);
          setPopupSuccessStudent(true);
        } else {
          setShowMessage(true);
          setMessageType("error");
          setShowMessageText(response.message);
        }
      },
      onError: (error: any) => {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(error.message ?? "Internal Error");
      },
    });

  const avatarSrc =
    isPending || !biography?.foto
      ? gender === "Wanita" || gender === "WANITA"
        ? FemaleLogo
        : MaleLogo
      : `https://ppalmanar.sipond.com/assets/fotosiswa/${biography.foto}`;

  useEffect(() => {
    const skip = localStorage.getItem("is-biometric");
    setIsBiometric(skip === "true");
  }, []);

  const onSetupBiometric = () => {
    const status = localStorage.getItem("status-biometric");
    if (status === "register") {
      androidSetupBiometric("remove");
    } else {
      androidSetupBiometric("register");
    }
  };

  const onLogout = () => {
    Cookies.remove("token");
    Cookies.remove("redirect");
    navigate("/auth/login");
    setPopupStatus(false);
    setAppBar({ show: false });
  };

  const onOpenPopupStudent = () => {
    setStudentNis(nis);
    setPopupStudent(true);
  };

  const onFinishChangeStudent = () => {
    queryClient.invalidateQueries();
    setPopupSuccessStudent(false);
  };

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  const barcodeRef = useCallback(
    (node: SVGSVGElement) => {
      if (node && isSuccessNis && nis) {
        node.innerHTML = "";
        try {
          JsBarcode(node as unknown as HTMLElement, nis, {
            format: "CODE128",
            width: 2.5,
            height: 100,
            displayValue: true,
          });
        } catch (error) {
          console.error("Barcode generation failed:", error);
        }
      }
    },
    [isSuccessNis, nis],
  );

  return (
    <>
      {contextHolder}
      {/* Green Header with Avatar */}
      <div
        className="tw-relative tw-overflow-hidden tw-px-5 tw-pt-6 tw-pb-10 tw-text-center"
        style={{
          background: "linear-gradient(155deg, #052e16 0%, #166534 70%)",
        }}
      >
        <div
          className="tw-absolute tw-rounded-full tw-pointer-events-none"
          style={{
            background: "#eab308",
            opacity: 0.08,
            width: 160,
            height: 160,
            top: -60,
            right: -30,
          }}
        />
        <div
          className="tw-absolute tw-rounded-full tw-pointer-events-none"
          style={{
            background: "#ffffff",
            opacity: 0.05,
            width: 80,
            height: 80,
            top: 8,
            right: 50,
          }}
        />

        <div className="tw-relative tw-z-10">
          <img
            src={avatarSrc}
            alt=""
            className="tw-w-20 tw-h-20 tw-object-cover tw-rounded-full tw-mx-auto tw-mb-3"
            style={{
              border: "3px solid rgba(255,255,255,0.4)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            }}
          />
          <div className="tw-text-[17px] tw-font-bold tw-text-white tw-leading-tight tw-mb-1">
            {biography?.namasiswa ?? nama ?? "---"}
          </div>
          <div
            className="tw-text-xs tw-font-medium"
            style={{ color: "#86efac" }}
          >
            Kelas {biography?.kelas ?? "-"}
          </div>
        </div>
      </div>

      <div className="tw-px-4 tw-pb-24 tw-mt-4">
        {/* Account Settings Card */}
        <div
          className="tw-rounded-2xl tw-bg-white tw-px-4 tw-pt-4 tw-pb-1 tw-mb-3"
          style={{
            boxShadow: "0 4px 20px rgba(22,101,52,0.09)",
            border: "1px solid #f0fdf4",
          }}
        >
          <SectionLabel title="Pengaturan Akun" />
          {students?.data && students.data.length > 1 && (
            <>
              <MenuRow
                icon={<LuUsers className="tw-text-lg tw-text-[#166534]" />}
                iconBg="#f0fdf4"
                label="Ganti Santri"
                sub="Merubah santri yang dipilih"
                onClick={onOpenPopupStudent}
              />
              <Divider />
            </>
          )}
          <MenuRow
            icon={<LuUserCog className="tw-text-lg tw-text-[#166534]" />}
            iconBg="#f0fdf4"
            label="Edit Profil"
            sub="Ubah data informasi siswa"
            onClick={() => navigate(linkPage.SETTING_UPDATE_ACCOUNT)}
          />
          <Divider />
          <MenuRow
            icon={<LuKeyRound className="tw-text-lg tw-text-[#3b82f6]" />}
            iconBg="#eff6ff"
            label="Ubah Kata Sandi"
            sub="Perbarui kata sandi akun"
            onClick={() =>
              navigate(linkPage.SETTING_CHANGE_PASSWORD, { state: dataNisData })
            }
          />
          <Divider />
          <MenuRow
            icon={<LuScanLine className="tw-text-lg tw-text-[#10b981]" />}
            iconBg="#ecfdf5"
            label="Barcode"
            sub="Tampilkan barcode NIS"
            onClick={() => setModalOpen(true)}
          />
        </div>

        {/* Preferences Card */}
        <div
          className="tw-rounded-2xl tw-bg-white tw-px-4 tw-pt-4 tw-pb-1"
          style={{
            boxShadow: "0 4px 20px rgba(22,101,52,0.09)",
            border: "1px solid #f0fdf4",
          }}
        >
          <SectionLabel title="Preferensi" />
          <MenuRow
            icon={<LuLogOut className="tw-text-lg tw-text-red-500" />}
            iconBg="#fff1f2"
            label="Keluar"
            sub="Keluar dari akun dengan aman"
            onClick={() => setPopupStatus(true)}
            danger
          />
          {BIOMETRIC_ENABLED && isBiometric && (
            <>
              <Divider />
              <MenuRow
                icon={
                  <LuFingerprint
                    className={`tw-text-lg ${isActiveBiometric ? "tw-text-[#0d9488]" : "tw-text-[#64748b]"}`}
                  />
                }
                iconBg={isActiveBiometric ? "#ecfdf5" : "#f1f5f9"}
                label={
                  isActiveBiometric
                    ? "Login Biometrik Aktif"
                    : "Aktifkan Login Biometrik"
                }
                sub={
                  isActiveBiometric
                    ? "Login cepat biometrik sudah aktif"
                    : "Fitur login cepat dengan biometrik"
                }
                onClick={onSetupBiometric}
              />
            </>
          )}
        </div>
      </div>

      <PopupConfirm
        open={popupStatus}
        title={nama || "-"}
        message="Yakin ingin keluar dari aplikasi?"
        textConfirm="Keluar"
        textCancel="Batalkan"
        onConfirm={onLogout}
        onCancel={() => setPopupStatus(false)}
      />

      <PopupCustom
        slideUpModal={false}
        open={modalOpen}
        title="Barcode NIS"
        onClose={() => setModalOpen(false)}
      >
        <div className="tw-flex tw-flex-col tw-items-center">
          <svg key={nis} ref={barcodeRef} />
          <Button
            onClick={() => setModalOpen(false)}
            className="tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
            style={{ background: "#ef4444" }}
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>

      <PopupCustom
        title="Ganti Santri"
        open={popupStudent}
        onCancel={() => setPopupStudent(false)}
        slideUpModal={true}
      >
        <Select
          className="tw-text-base"
          placeholder="Pilih Santri"
          value={studentNis || undefined}
          style={{ width: "100%" }}
          onChange={(value) => setStudentNis(value)}
          options={students?.data?.map((item) => ({
            value: item.nis,
            label: `${item.nama} - ${item.nis}`,
          }))}
        />
        <Button
          loading={isPendingChangeStudent}
          disabled={!studentNis || studentNis === nis}
          onClick={() => mutateChangeStudent(studentNis)}
          className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-sm tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
          style={{ background: "#166534" }}
        >
          Simpan
        </Button>
      </PopupCustom>

      <PopupSuccess
        open={popupSuccessStudent}
        title="Berhasil!"
        message="Ganti santri berhasil, sekarang semua data santri sudah berubah"
        textConfirm="Kembali"
        onConfirm={onFinishChangeStudent}
      />
    </>
  );
};
